/**
 * authStore.ts — mango-web
 * PURPOSE: Zustand store for auth state management.
 * Mirrors Android's OnboardingViewModel + SupabaseAuthClient — handles
 * sign-in, sign-up, magic link, password reset, OAuth, and session state.
 * Negative states handled first (error, loading) before content.
 */

import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { User, AuthError } from '@supabase/supabase-js';

// --- Types ---

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  magicLinkSent: boolean;
  resetPasswordSent: boolean;
}

interface AuthActions {
  initialize: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<boolean>;
  signUpWithEmail: (email: string, password: string, fullName: string) => Promise<boolean>;
  signInWithMagicLink: (email: string) => Promise<boolean>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
  clearMagicLinkSent: () => void;
  clearResetPasswordSent: () => void;
}

type AuthStore = AuthState & AuthActions;

// --- Helpers ---

function extractErrorMessage(error: AuthError): string {
  const msg = error.message.toLowerCase();
  if (msg.includes('invalid login')) return 'invalid email or password.';
  if (msg.includes('email not confirmed')) return 'please verify your email first.';
  if (msg.includes('user already registered')) return 'this email is already registered.';
  if (msg.includes('rate limit')) return 'too many attempts. please wait.';
  return error.message;
}

// --- Store ---

export const useAuthStore = create<AuthStore>((set, get) => ({
  // --- State (negatives first) ---
  error: null,
  isLoading: false,
  isInitialized: false,
  magicLinkSent: false,
  resetPasswordSent: false,
  user: null,

  // --- Actions ---

  /** Initialize auth — listen for session changes. Mirrors Android fast-path check. */
  initialize: async () => {
    console.debug('[AuthStore] initializing');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      set({
        user: session?.user ?? null,
        isInitialized: true,
        isLoading: false,
      });
      console.info('[AuthStore] initialized', session ? 'authenticated' : 'unauthenticated');

      // Listen for auth state changes (login, logout, token refresh)
      supabase.auth.onAuthStateChange((_event, session) => {
        console.debug('[AuthStore] auth state changed:', _event);
        set({ user: session?.user ?? null });
      });
    } catch (err) {
      console.warn('[AuthStore] init failed:', err);
      set({ isInitialized: true, isLoading: false });
    }
  },

  /** Email + password sign in. Mirrors SupabaseAuthClient.signInWithEmail(). */
  signInWithEmail: async (email, password) => {
    set({ isLoading: true, error: null });
    console.debug('[AuthStore] signInWithEmail attempt');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.warn('[AuthStore] signIn failed:', error.message);
      set({ isLoading: false, error: extractErrorMessage(error) });
      return false;
    }
    console.info('[AuthStore] signIn success');
    set({ isLoading: false });
    return true;
  },

  /** Email sign up with full name. Mirrors SupabaseAuthClient.signUpWithEmail(). */
  signUpWithEmail: async (email, password, fullName) => {
    set({ isLoading: true, error: null });
    console.debug('[AuthStore] signUpWithEmail attempt');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) {
      console.warn('[AuthStore] signUp failed:', error.message);
      set({ isLoading: false, error: extractErrorMessage(error) });
      return false;
    }
    console.info('[AuthStore] signUp success');
    set({ isLoading: false });
    return true;
  },

  /** Magic link sign in. Mirrors SupabaseAuthClient.signInWithMagicLink(). */
  signInWithMagicLink: async (email) => {
    set({ isLoading: true, error: null, magicLinkSent: false });
    console.debug('[AuthStore] sendMagicLink attempt');
    const { error } = await supabase.auth.signInWithOtp({ email });
    if (error) {
      console.warn('[AuthStore] magicLink failed:', error.message);
      set({ isLoading: false, error: extractErrorMessage(error) });
      return false;
    }
    console.info('[AuthStore] magicLink sent');
    set({ isLoading: false, magicLinkSent: true });
    return true;
  },

  /** Google OAuth. */
  signInWithGoogle: async () => {
    console.debug('[AuthStore] signInWithGoogle');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      console.warn('[AuthStore] google auth failed:', error.message);
      set({ error: extractErrorMessage(error) });
    }
  },

  /** Apple OAuth. Mirrors SupabaseAuthClient.signInWithApple(). */
  signInWithApple: async () => {
    console.debug('[AuthStore] signInWithApple');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      console.warn('[AuthStore] apple auth failed:', error.message);
      set({ error: extractErrorMessage(error) });
    }
  },

  /** Password reset email. Mirrors SupabaseAuthClient.resetPassword(). */
  resetPassword: async (email) => {
    set({ isLoading: true, error: null, resetPasswordSent: false });
    console.debug('[AuthStore] resetPassword attempt');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) {
      console.warn('[AuthStore] resetPassword failed:', error.message);
      set({ isLoading: false, error: extractErrorMessage(error) });
      return false;
    }
    console.info('[AuthStore] resetPassword email sent');
    set({ isLoading: false, resetPasswordSent: true });
    return true;
  },

  /** Sign out. Mirrors SupabaseAuthClient.signOut(). */
  signOut: async () => {
    console.debug('[AuthStore] signOut');
    await supabase.auth.signOut();
    set({ user: null, error: null });
    console.info('[AuthStore] signed out');
  },

  clearError: () => set({ error: null }),
  clearMagicLinkSent: () => set({ magicLinkSent: false }),
  clearResetPasswordSent: () => set({ resetPasswordSent: false }),
}));

// --- Related Files ---
// CONNECTED TO:
//   - src/lib/supabase.ts — Supabase client instance
// USED BY:
//   - src/routes/auth/Login.tsx — login form
//   - src/routes/auth/Signup.tsx — sign-up form
//   - src/components/AuthGuard.tsx — route protection
//   - src/App.tsx — initializes on mount
